import { createContext, PropsWithChildren, useContext, useState } from 'react';

const ActivityPaneContext = createContext(false);

/**
 * Captures whether this miniapp instance is framed inside tenant-portal's activity-center pane,
 * flagged by an `activity-pane=true` marker the host appends to the launch URL.
 *
 * Read from the boot URL exactly once (lazy state), and never persisted: a later in-app navigation
 * or redirect that drops the query cannot clear it, and a fresh standalone load reads its own URL and
 * starts clean rather than inheriting a prior pane visit. Reads `window.location` directly so it does
 * not depend on the router and can sit anywhere in the tree.
 */
export const ActivityPaneProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const [isActivityPane] = useState(() => new URLSearchParams(window.location.search).get('activity-pane') === 'true');

  return <ActivityPaneContext.Provider value={isActivityPane}>{children}</ActivityPaneContext.Provider>;
};

/** True while embedded in the activity-center pane; defaults to false (standalone) with no provider. */
export const useIsActivityPane = (): boolean => useContext(ActivityPaneContext);
